// AutoCompleteFilterTextarea.tsx - SIMPLIFIED VERSION
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  InfiniteScroll,
  Textarea,
  useBlockDialogEscape,
} from '@ais-ui/ui-core';
import { X } from 'lucide-react';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useFilterContext } from '../../Map/FilterContext';
import { useShapesList } from '@ais-ui/api';
import { filterAndFlattenShapes } from '../../Shapes/hooks/useFilteredAndFlattenedShapes';

// Import new hooks and utilities
import { useFilterAutocomplete } from './utils/useFilterAutocomplete';
import { useAutocompleteKeyboard } from './utils/useAutocompleteKeyboard';
import {
  calculateDropdownHeight,
  getSuggestionTypeStyles,
  getSuggestionDisplayInfo,
} from './utils/autocompleteUtils';
import { Suggestion } from './utils/types';

interface AutoCompleteFilterTextareaProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const AutoCompleteFilterTextarea: React.FC<
  AutoCompleteFilterTextareaProps
> = ({ id, value, onChange, placeholder }) => {
  const { messages } = useLocalization();
  const { filterOptions } = useFilterContext();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [scrollViewport, setScrollViewport] = useState<HTMLDivElement | null>(
    null,
  );
  const suggestionPopupRef = useRef<HTMLDivElement>(null);

  // Fetch shapes data
  const { data: rawShapeData } = useShapesList();
  const shapeData = useMemo(() => {
    const shapes = rawShapeData?.records ? rawShapeData.records : [];
    if (!shapes) return [];
    return filterAndFlattenShapes(shapes, '');
  }, [rawShapeData]);

  // Use the autocomplete hook
  const {
    isAutocompleteOpen,
    cursorPosition,
    focusedSuggestionIndex,
    suggestions,
    currentToken,
    expectedNext,
    setIsAutocompleteOpen,
    setFocusedSuggestionIndex,
    handleSelectSuggestion,
    handleChange,
    handleSelect,
    handleFocus,
    handleBlur,
  } = useFilterAutocomplete({
    value,
    onChange,
    filterOptions,
    shapeData,
    textAreaRef,
  });

  // Handle blur event to close suggestions
  const handleBlurEvent = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Check if the focus is moving to an element within the suggestion popup
      const relatedTarget = e.relatedTarget as HTMLElement;

      if (relatedTarget && suggestionPopupRef.current) {
        // If the focus is moving to an element inside the popup, don't close
        if (suggestionPopupRef.current.contains(relatedTarget)) {
          return;
        }
      }

      // Call the hook's blur handler to update focus state
      handleBlur();

      // Close the suggestions (this will happen automatically due to focus state change)
      setIsAutocompleteOpen(false);
    },
    [handleBlur, setIsAutocompleteOpen],
  );

  // Use keyboard navigation hook
  const { handleKeyDown } = useAutocompleteKeyboard({
    isAutocompleteOpen,
    suggestions,
    focusedSuggestionIndex,
    setIsAutocompleteOpen,
    setFocusedSuggestionIndex,
    handleSelectSuggestion,
  });

  // Block dialog escape when autocomplete is open
  useBlockDialogEscape(isAutocompleteOpen);

  // Get display info for suggestions
  const { isShowingHints, headerText, emptyText } = useMemo(
    () =>
      getSuggestionDisplayInfo(
        suggestions,
        currentToken,
        expectedNext,
        value,
        messages,
      ),
    [suggestions, currentToken, expectedNext, value, messages],
  );

  // Calculate dropdown height
  const calculatedHeight = calculateDropdownHeight(suggestions.length);

  // Reset focused index when suggestions change
  useEffect(() => {
    if (suggestions.length > 0) {
      const firstSelectableIndex = suggestions.findIndex((s) => !s.isHint);
      setFocusedSuggestionIndex(
        firstSelectableIndex >= 0 ? firstSelectableIndex : 0,
      );
    } else {
      setFocusedSuggestionIndex(0);
    }
  }, [suggestions, setFocusedSuggestionIndex]);

  // Handle scrolling when focused suggestion changes
  useEffect(() => {
    if (isAutocompleteOpen && scrollViewport) {
      const itemHeight = 36;
      const containerHeight = calculatedHeight;
      const maxVisibleItems = Math.floor(containerHeight / itemHeight);

      if (focusedSuggestionIndex >= maxVisibleItems) {
        const scrollTop =
          (focusedSuggestionIndex - maxVisibleItems + 1) * itemHeight;
        scrollViewport.scrollTop = scrollTop;
      } else if (
        focusedSuggestionIndex <
        scrollViewport.scrollTop / itemHeight
      ) {
        scrollViewport.scrollTop = focusedSuggestionIndex * itemHeight;
      }
    }
  }, [
    focusedSuggestionIndex,
    isAutocompleteOpen,
    calculatedHeight,
    scrollViewport,
  ]);

  // Render a single suggestion item
  const renderSuggestionItem = (suggestion: Suggestion, index: number) => {
    const Icon = suggestion.icon;
    const isSelected = focusedSuggestionIndex === index;
    const isHint = suggestion.isHint;

    return (
      <div
        className={cn(
          'p-2 text-sm rounded-md flex items-center gap-2',
          isHint
            ? 'cursor-default opacity-75'
            : 'cursor-pointer hover:bg-accent',
          isSelected && !isHint ? 'bg-accent' : '',
        )}
        onClick={() => !isHint && handleSelectSuggestion(suggestion)}
        role={isHint ? 'presentation' : 'button'}
        tabIndex={isHint ? -1 : 0}
      >
        <span
          className={cn(
            'px-2 py-0.5 text-xs rounded-sm font-medium shrink-0',
            isHint
              ? 'bg-muted text-muted-foreground'
              : getSuggestionTypeStyles(suggestion.type),
          )}
        >
          {isHint ? 'hint' : suggestion.type}
        </span>

        <div className="flex items-center gap-2 flex-1">
          {Icon && <Icon className="size-4 shrink-0" />}
          <span
            className={cn(
              isHint ? 'italic text-muted-foreground' : 'font-medium',
            )}
          >
            {suggestion.label}
          </span>
        </div>

        {suggestion.description && (
          <span className="text-xs text-muted-foreground ml-4">
            {suggestion.description}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="relative h-[132px] space-y-2">
      <Textarea
        ref={textAreaRef}
        id={id}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        onFocus={handleFocus} // Track when textarea gets focus
        onBlur={handleBlurEvent} // Close suggestions on blur
        placeholder={placeholder}
        className="font-mono text-sm h-full"
        autoComplete="off"
        spellCheck={false}
      />

      {/* Autocomplete dropdown */}
      {isAutocompleteOpen && (
        <div className="absolute w-full border mt-1 bg-background rounded-md shadow-md z-50">
          <div className="relative p-4" ref={suggestionPopupRef}>
            <div className="flex justify-between mb-4">
              <h4 className="text-sm font-medium leading-none">{headerText}</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAutocompleteOpen(false)}
                className="h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>

            <div
              className="w-full"
              style={
                {
                  '--scroll-height': `${calculatedHeight}px`,
                } as React.CSSProperties
              }
            >
              {suggestions.length > 0 ? (
                <InfiniteScroll
                  items={suggestions.map((suggestion, index) => ({
                    suggestion,
                    index,
                  }))}
                  renderItem={({ suggestion, index }) =>
                    renderSuggestionItem(suggestion, index)
                  }
                  limit={20}
                  observeResize={false}
                  className="pr-2 h-[var(--scroll-height)]"
                  onScrollViewportRef={setScrollViewport}
                />
              ) : (
                <div className="text-sm text-muted-foreground p-2">
                  {emptyText}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
